import { apiConfig } from "@/config/env";
import { logger } from "@/lib/logger";

// API Response types based on backend schema
export interface ApiAgent {
  id: string;
  title: string; // API returns 'title', not 'name'
  description: string;
  competencies: string[]; // API returns 'competencies' array, not 'competency' string
  tags: string[];
  githubUrl?: string;
  websiteUrl?: string;
  markdownContent?: string;
  authorName?: string;
  authorEmail?: string;
  authorId?: string;
  createdAt: string;
  updatedAt: string;
  status?: string;
  stats?: {
    likes: number;
    views: number;
  };
  likes?: number; // Keep for backward compatibility
  views?: number; // Keep for backward compatibility
  // Top-level metadata fields (new structure)
  license?: string;
  githubStars?: number;
  primaryLanguage?: string;
  repositoryCreatedAt?: string;
  repositoryUpdatedAt?: string;
  forksCount?: number;
  openIssuesCount?: number;
  watchersCount?: number;
  hasWiki?: boolean;
  hasPages?: boolean;
  githubTopics?: string[];
  // Legacy metadata object (for backward compatibility)
  metadata?: {
    github_stars?: number;
    license?: string;
    language?: string;
    created_at?: string;
    updated_at?: string;
  };
}

export interface ApiAgentsResponse {
  agents: ApiAgent[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export interface ApiCompetenciesResponse {
  competencies: Array<{
    id: string;
    name: string;
    description: string;
    agentCount?: number;
    category?: string;
    skills?: string[];
    createdAt?: string;
  }>;
}

export interface ApiTagsResponse {
  tags: Array<{
    id: string;
    name: string;
    description?: string;
    usageCount?: number;
    category?: string;
    createdAt?: string;
  }>;
}

export interface ApiHealthResponse {
  status: string;
  timestamp: string;
  service: string;
  version: string;
  environment: string;
  region: string;
  database: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

class ApiService {
  private baseUrl: string;
  private apiKey: string;
  private healthUrl: string;

  constructor() {
    this.baseUrl = apiConfig.BASE_URL;
    this.apiKey = apiConfig.API_KEY;
    this.healthUrl = apiConfig.HEALTH_URL;

    logger.log("🔧 API Service initialized:", {
      baseUrl: this.baseUrl,
      healthUrl: this.healthUrl,
      hasApiKey: !!this.apiKey,
    });
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers = {
      "Content-Type": "application/json",
      "X-API-Key": this.apiKey,
      ...options.headers,
    };

    logger.api.request(
      options.method || "GET",
      url,
      options.body ? JSON.parse(options.body as string) : undefined,
    );

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      logger.log(
        "📥 API response status:",
        response.status,
        response.statusText,
      );

      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get("content-type");

        try {
          // Check if response is JSON before trying to parse it
          if (contentType && contentType.includes("application/json")) {
            errorData = await response.json();
          } else {
            // If it's HTML or other content, get text and create a generic error
            const textResponse = await response.text();
            if (
              textResponse.includes("<!DOCTYPE") ||
              textResponse.includes("<html>")
            ) {
              // This is an HTML error page, likely from a reverse proxy or CDN
              errorData = {
                message: "Service temporarily unavailable",
                code: "SERVICE_UNAVAILABLE",
                details:
                  "The backend service is currently unavailable. Please try again later.",
              };
            } else {
              errorData = textResponse;
            }
          }
        } catch {
          // If parsing fails completely, provide a fallback error
          errorData = {
            message: "Service temporarily unavailable",
            code: "SERVICE_UNAVAILABLE",
            details: "Unable to connect to the backend service.",
          };
        }

        logger.error("❌ API error response:", errorData);

        // Extract error message and details for better UX
        let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
        if (typeof errorData === "object" && errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }

        throw new ApiError(errorMessage, response.status, errorData);
      }

      // Check if successful response is JSON before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        if (
          textResponse.includes("<!DOCTYPE") ||
          textResponse.includes("<html>")
        ) {
          throw new ApiError(
            "Service temporarily unavailable - received HTML instead of JSON",
            200,
            "HTML_RESPONSE",
          );
        }
        // Try to parse as JSON anyway for backward compatibility
        try {
          const data = JSON.parse(textResponse);
          logger.api.response(options.method || "GET", url, data);
          return data;
        } catch {
          throw new ApiError(
            "Invalid response format - expected JSON",
            200,
            "INVALID_FORMAT",
          );
        }
      }

      const data = await response.json();
      logger.api.response(options.method || "GET", url, data);
      return data;
    } catch (error) {
      logger.error("💥 Network error:", error);
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle common network errors with user-friendly messages
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        throw new ApiError(
          "Unable to connect to the service. Please check your internet connection and try again.",
          0,
        );
      }

      if (
        error instanceof SyntaxError &&
        error.message.includes("Unexpected token")
      ) {
        throw new ApiError(
          "Service temporarily unavailable. Please try again later.",
          0,
        );
      }

      throw new ApiError(
        `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
        0,
      );
    }
  }

  // Health check (no API key required)
  async healthCheck(): Promise<ApiHealthResponse> {
    const response = await fetch(this.healthUrl);
    if (!response.ok) {
      throw new ApiError(
        `Health check failed: ${response.status}`,
        response.status,
      );
    }
    return await response.json();
  }

  // Get agents with pagination
  async getAgents(
    limit = 6,
    offset = 0,
    search?: string,
    tags?: string[],
    competency?: string,
    sort?: string,
  ): Promise<ApiAgentsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (search) {
      params.append("search", search);
    }

    if (tags && tags.length > 0) {
      params.append("tags", tags.join(","));
    }

    if (competency) {
      params.append("competency", competency);
    }

    if (sort) {
      params.append("sort", sort);
    }

    return this.makeRequest<ApiAgentsResponse>(`/agents?${params.toString()}`);
  }

  // Get single agent by ID
  async getAgent(id: string): Promise<ApiAgent> {
    // API returns the agent directly, not wrapped in { agent: ... }
    return this.makeRequest<ApiAgent>(`/agents/${id}`);
  }

  // Create new agent
  async createAgent(agentData: {
    name: string;
    description: string;
    competency: string;
    tags: string[];
    githubUrl?: string;
    websiteUrl?: string;
    markdownContent?: string;
    authorName: string;
    authorEmail?: string;
  }): Promise<ApiAgent> {
    // Transform the data to match what the Lambda function expects
    const transformedData = {
      title: agentData.name, // Lambda expects 'title', not 'name'
      description: agentData.description,
      competencies: [agentData.competency], // Lambda expects 'competencies' array, not 'competency' string
      tags: agentData.tags,
      githubUrl: agentData.githubUrl,
      websiteUrl: agentData.websiteUrl,
      markdownContent: agentData.markdownContent,
      authorName: agentData.authorName,
      authorEmail: agentData.authorEmail,
    };

    const response = await this.makeRequest<ApiAgent>("/agents", {
      method: "POST",
      body: JSON.stringify(transformedData),
    });
    return response;
  }

  // Update agent (requires authentication)
  async updateAgent(
    id: string,
    agentData: Partial<ApiAgent>,
  ): Promise<ApiAgent> {
    const response = await this.makeRequest<ApiAgent>(`/agents/${id}`, {
      method: "PUT",
      body: JSON.stringify(agentData),
    });
    return response;
  }

  // Delete agent (requires authentication)
  async deleteAgent(id: string): Promise<void> {
    await this.makeRequest(`/agents/${id}`, {
      method: "DELETE",
    });
  }

  // Like an agent
  async likeAgent(id: string): Promise<void> {
    // Client-side (localStorage) handles rate limiting
    // No need to handle 409 errors anymore
    await this.makeRequest(`/agents/${id}/like`, {
      method: "POST",
    });
  }

  // Track agent view
  async viewAgent(id: string): Promise<void> {
    await this.makeRequest(`/agents/${id}/view`, {
      method: "POST",
    });
  }

  // Get competencies
  async getCompetencies(): Promise<ApiCompetenciesResponse> {
    return this.makeRequest<ApiCompetenciesResponse>("/competencies");
  }

  // Get tags
  async getTags(): Promise<ApiTagsResponse> {
    return this.makeRequest<ApiTagsResponse>("/tags");
  }

  // Search agents
  async searchAgents(query: string, limit = 20): Promise<ApiAgentsResponse> {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
    });

    return this.makeRequest<ApiAgentsResponse>(`/search?${params.toString()}`);
  }

  // Get analytics
  async getAnalytics(): Promise<{
    totalAgents: number;
    totalViews: number;
    totalLikes: number;
    topTags: Array<{ name: string; count: number }>;
  }> {
    return this.makeRequest("/analytics/stats");
  }

  // Get trending agents
  async getTrendingAgents(): Promise<ApiAgentsResponse> {
    return this.makeRequest<ApiAgentsResponse>("/analytics/trending");
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Export for easier importing
export default apiService;
